import {
  component,
  computed,
  observable,
  subscribe,
  unwrap,
} from 'knockout-decorators'

import { CartResponse } from '@tixa/schema'
import { CartState } from './state'
import type { ModalOptions } from './modal'
import { Language } from '~/utils'
import { TouristTaxState } from './state/tourist-tax'
import { PageState } from '~/store/page'
import {
  useOrderStore,
  isPaymentInitiatedForCartSync,
} from './state/store-order'
import { PaymentChecker } from '../payment-checker/payment-checker'

import './user'
import './form'
import './terms'
import './summary'
import './payment'
import './shipping'
import './queue'

import './modal'
import './modal-content'

import './error'
import './error/footer'
import './expiration'
import './tourist-tax/tourist-tax-form'
import './tourist-tax/tourist-tax-content'
import './tourist-tax/tourist-tax-guest'

declare const EMBED: boolean

import source from './cart.html?raw'
import './cart.less'

@component('cart', source)
export class Cart {
  private modalSettings = {
    backdrop: 'static',
    show: false,
    focus: true,
    keyboard: false,
  }

  // Cart state
  private cartState: CartState = CartState.get()
  private touristTaxState: TouristTaxState = TouristTaxState.get()
  private orderStore = useOrderStore()
  private Locale = Language.getInstance()
  private beaconSent = false
  private isPaymentRedirecting = false
  public i18n(key: string): string {
    return this.Locale.translations[key]
  }

  @observable private isLoading = false

  @computed get isModalShown() {
    return (
      this.cartState.content &&
      !this.cartState.hasError(
        this.cartState.content,
        this.cartState.order,
        this.cartState.hasCartExpired
      )
    )
  }

  @computed get isErrorModalShown() {
    return this.cartState.error !== null && !this.cartState.hasCartExpired
  }

  @computed get isExpiryModalShown() {
    return (
      (this.cartState.hasCartExpired && !this.isErrorModalShown) ||
      (this.touristTaxState.hasTouristTaxExpired &&
        !this.isTouristTaxErrorModalShown)
    )
  }

  @computed get isTouristTaxModalShown() {
    return this.touristTaxState.showTouristTaxForm
  }

  @computed get isTouristTaxErrorModalShown() {
    return this.touristTaxState.error !== null
  }

  private get modalOptions(): ModalOptions {
    return {
      titleKey: 'cart',
      bsModalSettings: this.modalSettings,
      show: unwrap<boolean>(this, 'isModalShown'),
      size: 'xl',
      body: {
        name: 'cart-modal-content',
      },
      hidden: () => {
        this.modalHidden()
      },
    }
  }
  private get expiryModalOptions(): ModalOptions {
    return {
      titleKey: 'timeout',
      bsModalSettings: { ...this.modalSettings, show: true },
      show: unwrap<boolean>(this, 'isExpiryModalShown'),
      body: {
        name: 'cart-expiration-content',
      },
      footer: {
        name: 'cart-reload-footer',
      },
      hidden: () => {
        window.location.reload()
      },
    }
  }

  private get errorModalOptions(): ModalOptions {
    const icons = {
      couponused: 'locked',
      onecart: 'onecart',
      error: 'error',
      soldout: 'expired',
      timesup: 'expired',
      kuponlejart: 'error',
      kuponhasznalt: 'error',
      kodlejart: 'error',
      nemmehet: 'error',
    }

    const titles = {
      couponused: 'error',
      onecart: 'error',
      error: 'error',
      soldout: 'soldout',
      timesup: 'timeout',
      kuponlejart: 'error',
      kuponhasznalt: 'error',
      kodlejart: 'error',
      nemmehet: 'error',
    }

    const messages = {
      couponused: 'couponused',
      onecart: 'onecart',
      error: 'waiterror',
      soldout: 'cartsoldout',
      timesup: 'timesup',
      kuponlejart: 'ercouponusedror',
      kuponhasznalt: 'couponused',
      kodlejart: 'couponused',
      nemmehet: 'error',
    }

    const hasTitleKey =
      this.cartState.error !== null && this.cartState.error in titles

    const error = this.cartState.error || 'error'

    return {
      titleKey: titles[hasTitleKey ? error : 'error'],
      bsModalSettings: { ...this.modalSettings, show: true },
      show: unwrap<boolean>(this, 'isErrorModalShown'),
      body: {
        name: 'cart-error-content',
        params: {
          message:
            this.cartState.content?.message ||
            this.cartState.order?.reason ||
            Language.getInstance().translations[messages[error]],
          icon: icons[error],
        },
      },
      footer: {
        name: 'cart-reload-footer',
      },
      hidden: () => {
        this.cartState.clear()
        window.location.reload()
      },
    }
  }

  private get touristTaxModalOptions(): ModalOptions {
    return {
      title: this.touristTaxState.isInQueue ? '' : this.i18n('touristTaxInfo'),
      bsModalSettings: this.modalSettings,
      show: unwrap<boolean>(this, 'isTouristTaxModalShown'),
      size: 'lg',
      body: {
        name: 'tourist-tax-form',
      },
      hidden: () => {
        this.touristTaxModalHidden()
      },
    }
  }

  private get touristTaxErrorModalOptions(): ModalOptions {
    const titles = {
      error: 'error',
    }

    const messages = {
      error: 'waiterror',
    }

    const icons = {
      error: 'error',
    }

    const error = this.touristTaxState.error || 'error'
    const hasTitleKey =
      this.touristTaxState.error !== null &&
      this.touristTaxState.error in titles
    return {
      titleKey: titles[hasTitleKey ? error : 'error'],
      bsModalSettings: { ...this.modalSettings, show: true },
      show: unwrap<boolean>(this, 'isTouristTaxErrorModalShown'),
      body: {
        name: 'cart-error-content',
        params: {
          message:
            this.touristTaxState.submissionErrorMessage ||
            Language.getInstance().translations[messages[error]],
          icon: icons[error],
        },
      },
      footer: {
        name: 'cart-reload-footer',
      },
      hidden: async () => {
        await this.cartState.clear()
        await this.touristTaxState.clear()
        window.location.reload()
      },
    }
  }

  constructor() {
    subscribe<CartResponse>(
      () => this.isModalShown,
      (isShown) => {
        isShown
          ? this.modalShownSideEffect('cart-open')
          : this.modalHiddenSideEffect('cart-open')
      }
    )

    subscribe<boolean>(
      () => this.isTouristTaxModalShown,
      (isTouristTaxModalShown) => {
        isTouristTaxModalShown
          ? this.modalShownSideEffect('tourist-tax-open')
          : this.modalHiddenSideEffect('tourist-tax-open')
      }
    )

    subscribe<string | null>(
      () => this.cartState.error,
      (error) => {
        if (error) {
          this.cleanupWaitingRoomOnError()
        }
      }
    )

    subscribe<string | null>(
      () => this.touristTaxState.error,
      (error) => {
        if (error) {
          this.cleanupWaitingRoomOnError()
        }
      }
    )
  }

  private modalShownSideEffect(classList: string) {
    if (EMBED) {
      window.parent.postMessage({ action: 'setModal', open: true }, '*')
    }
    document.body.classList.add(classList)

    this.beaconSent = false
    this.isPaymentRedirecting = false
    window.addEventListener('pagehide', this.handlePageUnload)
    window.addEventListener('pageshow', this.handlePageShow)
    window.addEventListener(
      'payment-redirect-starting',
      this.handlePaymentRedirect
    )
  }

  private modalHiddenSideEffect(classList: string) {
    document.body.classList.remove(classList)
    this.isPaymentRedirecting = false
    window.removeEventListener('pagehide', this.handlePageUnload)
    window.removeEventListener('pageshow', this.handlePageShow)
    window.removeEventListener(
      'payment-redirect-starting',
      this.handlePaymentRedirect
    )
  }

  private handlePaymentRedirect = () => {
    this.isPaymentRedirecting = true
    window.removeEventListener('pagehide', this.handlePageUnload)
  }

  private handlePageShow = async (event: PageTransitionEvent) => {
    // Check if page was restored from bfcache
    if (event.persisted) {
      if (this.isPaymentRedirecting) {
        const hasOrder = await this.orderStore.hasOrderData()
        if (hasOrder) {
          const orderData = await this.orderStore.getOrderData()
          if (orderData?.orderId) {
            await this.cartState.clear()
            const paymentChecker = new PaymentChecker()
            paymentChecker.checkPayment(orderData.orderId)
          }
        }
        this.isPaymentRedirecting = false
      }
    }
  }

  private deleteWaitingRoom = () => {
    if (this.beaconSent) {
      return
    }

    const cartId = this.cartState.content?.id
    const touristTaxCartId = this.touristTaxState.cartResponse?.id
    const pageState = PageState.get()

    const deleteCart = cartId != null && !isPaymentInitiatedForCartSync(cartId)
    const deleteTouristCart =
      touristTaxCartId != null &&
      touristTaxCartId !== cartId &&
      !isPaymentInitiatedForCartSync(touristTaxCartId)

    if (!deleteCart && !deleteTouristCart) {
      return
    }

    this.beaconSent = true

    if (deleteCart) {
      const { fullUrl, payload } =
        pageState.api.waitingRoom.getDeleteRequestConfig(cartId)
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      })
      navigator.sendBeacon(fullUrl, blob)
    }

    if (deleteTouristCart) {
      const { fullUrl, payload } =
        pageState.api.waitingRoom.getDeleteRequestConfig(touristTaxCartId)
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      })
      navigator.sendBeacon(fullUrl, blob)
    }
  }

  private cleanupWaitingRoomOnError = () => {
    this.deleteWaitingRoom()
  }

  private handlePageUnload = (event: PageTransitionEvent) => {
    if (this.isPaymentRedirecting) {
      return
    }

    const cartId = this.cartState.content?.id
    if (cartId != null && isPaymentInitiatedForCartSync(cartId)) {
      return
    }

    if (event.type === 'pagehide' && !event.persisted) {
      this.deleteWaitingRoom()
    }
  }

  private modalHidden = async () => {
    this.modalHiddenSideEffect('cart-open')
    if (EMBED) {
      window.parent.postMessage({ action: 'setModal', open: false }, '*')
    }
    if (this.cartState.content && this.cartState.error) {
      await this.cartState.deleteCart()
      return
    }
    if (this.cartState.content) {
      await this.cartState.clear()
    }
  }

  private touristTaxModalHidden = async () => {
    this.modalHiddenSideEffect('tourist-tax-open')
    if (EMBED) {
      window.parent.postMessage({ action: 'setModal', open: false }, '*')
    }

    if (this.touristTaxState.cartResponse && this.touristTaxState.error) {
      await this.touristTaxState.deleteCart()
      return
    }
    if (this.touristTaxState.showTouristTaxForm) {
      await this.touristTaxState.clear()
    }
  }
}
